import type { JSX } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@artosera/ui';
import { useSession } from '../../auth/useSession.js';
import {
  creerBeneficiaire,
  listerBeneficiaires,
  modifierBeneficiaire,
  supprimerBeneficiaire,
} from '../../lib/api.js';
import { pourcentageEnSaisie } from '../../lib/montant.js';
import type { BeneficiaireSaisie } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import { EtatOnglet, useMessageErreur } from '../Finances/commun.js';
import { TableauSaisie, type ChampSaisie } from '../Finances/TableauSaisie.js';
import styles from './Associes.module.css';
import { useAssocies } from './useAssocies.js';

/**
 * Le partage entre associés : un bénéficiaire par ligne, sa part saisie sur
 * 100 %. Les parts ne sont JAMAIS calculées ni proposées : une part vide reste
 * « à saisir », et l'API refuse toute saisie qui ferait dépasser 100 %
 * (`parts_depassees`, affiché sur la ligne).
 */
export function PartageAssocies(): JSX.Element {
  const { t } = useTranslation('console');
  const message = useMessageErreur('partage');
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  const client = useQueryClient();
  const { comptes } = useAssocies();

  const cle = ['console', 'partage', 'beneficiaires', compteId] as const;
  const requete = useQuery({ queryKey: cle, queryFn: listerBeneficiaires });
  const relire = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: cle }),
      client.invalidateQueries({ queryKey: ['console', 'partage', 'versements'] }),
      client.invalidateQueries({ queryKey: ['console', 'vue-d-aigle'] }),
    ]);
  };
  const creer = useMutation({ mutationFn: creerBeneficiaire, onSuccess: relire });
  const modifier = useMutation({
    mutationFn: ({ id, corps }: { id: string; corps: Record<string, unknown> }) =>
      modifierBeneficiaire(id, corps),
    onSuccess: relire,
  });
  const supprimer = useMutation({ mutationFn: supprimerBeneficiaire, onSuccess: relire });

  const lignes = requete.data ?? [];
  const nomsComptes = useMemo(
    () => new Map(comptes.map((compte) => [compte.id, compte.nom])),
    [comptes],
  );

  // Seuls les comptes sans invitation en attente portent le rôle d'associé :
  // l'API refuse tout autre `compteId` (`compte_non_associe`).
  const champs: ChampSaisie[] = [
    {
      cle: 'libelle',
      titre: t('associes.partage.champs.libelle'),
      type: 'texte',
      requis: true,
      longueurMax: 120,
    },
    {
      cle: 'compteId',
      titre: t('associes.partage.champs.compte'),
      type: 'choix',
      optionVide: t('associes.partage.aucunCompte'),
      options: comptes
        .filter((compte) => !compte.invitationEnAttente)
        .map((compte) => ({ value: compte.id, label: compte.nom })),
    },
    { cle: 'partPointsDeBase', titre: t('associes.partage.champs.part'), type: 'pourcentage' },
  ];

  const saisies = lignes.filter((ligne) => ligne.partPointsDeBase !== null);
  const totalPoints = saisies.reduce((somme, ligne) => somme + (ligne.partPointsDeBase ?? 0), 0);
  const partsASaisir = lignes.length - saisies.length;

  return (
    <section className={pageStyles.panneau} aria-labelledby="associes-partage">
      <div className={pageStyles.sectionTitre}>
        <h2 id="associes-partage">{t('associes.partage.titre')}</h2>
      </div>

      {requete.isLoading || requete.isError ? (
        <EtatOnglet chargement={requete.isLoading} enErreur={requete.isError} />
      ) : (
        <TableauSaisie<BeneficiaireSaisie>
          legende={t('associes.partage.titre')}
          introduction={t('associes.partage.texte')}
          lignes={lignes}
          colonnes={[
            {
              cle: 'beneficiaire',
              titre: t('associes.partage.colonnes.beneficiaire'),
              rendu: (ligne) => ligne.libelle,
            },
            {
              cle: 'compte',
              titre: t('associes.partage.colonnes.compte'),
              rendu: (ligne) =>
                (ligne.compteId ? nomsComptes.get(ligne.compteId) : undefined) ?? '—',
            },
            {
              cle: 'part',
              titre: t('associes.partage.colonnes.part'),
              numerique: true,
              rendu: (ligne) =>
                ligne.partPointsDeBase === null ? (
                  <Badge tone="reserved">{t('saisies.aSaisir')}</Badge>
                ) : (
                  `${pourcentageEnSaisie(ligne.partPointsDeBase)} %`
                ),
            },
          ]}
          champs={champs}
          valeursDe={(ligne) => ({
            libelle: ligne.libelle,
            compteId: ligne.compteId,
            partPointsDeBase: ligne.partPointsDeBase,
          })}
          libelleLigne={(ligne) => ligne.libelle}
          enEcriture
          libelleAjouter={t('associes.partage.ajouter')}
          messageVide={t('associes.partage.vide')}
          surCreer={(corps) => creer.mutateAsync(corps)}
          surModifier={(ligne, changements) =>
            modifier.mutateAsync({ id: ligne.id, corps: changements })
          }
          surSupprimer={(ligne) => supprimer.mutateAsync(ligne.id)}
          message={message}
          pied={
            <p className={styles.totalParts}>
              <span>
                {t('associes.partage.total', { total: pourcentageEnSaisie(totalPoints) || '0' })}
              </span>
              {partsASaisir > 0 ? (
                <span className={styles.partsASaisir}>
                  {t('associes.partage.partsASaisir', { count: partsASaisir })}
                </span>
              ) : null}
            </p>
          }
        />
      )}
    </section>
  );
}
